import {
  Box,
  Table as TableDS,
  TableContainer,
  TablePagination,
} from '@cambridgeassessment/cambridge-ui';
import React, { memo, useCallback, useMemo } from 'react';
import { hot } from 'react-hot-loader/root';
import { TooltipContent } from 'src/types';
import TableBody from './TableBody';
import TableBodyMessage from './TableBodyMessage';
import TableHead from './TableHead';
import TableLoader from './TableLoader';
import { HeadCell, Order, Row, Settings } from './types';
import { getComparator, stableSort } from './utils/sorting';

export const ROWS_PER_PAGE_OPTIONS = [15, 25, 50];

type Props = {
  headCells: HeadCell[];
  rows: Row[];
  sortColumns?: (
    order: Order,
    orderBy: string
  ) => (
    a: {
      [x: string]: unknown;
    },
    b: {
      [x: string]: unknown;
    }
  ) => number;
  settings: Settings;
  updateSettings: (value: Settings) => void;
  error?: boolean;
  loading?: boolean;
  tooltips?: TooltipContent;
  className?: string;
  noResultsMessage?: React.ReactNode;
};

const Table: React.FC<Props> = ({
  headCells,
  rows,
  sortColumns,
  settings,
  updateSettings,
  loading,
  error,
  tooltips,
  className,
  noResultsMessage,
}) => {
  const { order, orderBy, page, rowsPerPage } = settings;

  const handlePageChange = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) =>
      updateSettings({ ...settings, page: newPage }),
    [updateSettings, settings]
  );

  const handleRequestSort = useCallback(
    (
      _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      property: string
    ) => {
      const isAsc =
        settings.orderBy === property && settings.order === Order.Asc;
      updateSettings({
        ...settings,
        order: isAsc ? Order.Desc : Order.Asc,
        orderBy: property,
      });
    },
    [updateSettings, settings]
  );

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
      updateSettings({
        ...settings,
        rowsPerPage: parseInt(event.target.value, 10),
        page: 0,
      }),
    [updateSettings, settings]
  );

  const sortedRows = useCallback(
    () =>
      stableSort<Row>(
        rows,
        sortColumns
          ? sortColumns(order, orderBy)
          : // eslint-disable-next-line @typescript-eslint/no-explicit-any
            getComparator<any>(order, orderBy)
      ).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rows, rowsPerPage, sortColumns]
  );

  const emptyRows = useMemo(
    () =>
      rowsPerPage < rows?.length
        ? rowsPerPage - Math.min(rowsPerPage, rows?.length - page * rowsPerPage)
        : 0,
    [rowsPerPage, rows?.length, page]
  );

  const renderBody = useCallback(() => {
    if (loading) {
      return <TableLoader colSpan={headCells.length} />;
    }
    if (error) {
      return <TableBodyMessage error colSpan={headCells.length} />;
    }
    if (rows.length) {
      return (
        <TableBody
          rows={sortedRows()}
          emptyRows={emptyRows}
          data-testid="sorting-table-body"
        />
      );
    }
    if (!rows.length) {
      return (
        <TableBodyMessage
          colSpan={headCells.length}
          noResultsMessage={noResultsMessage}
        />
      );
    }
  }, [
    loading,
    error,
    emptyRows,
    headCells.length,
    noResultsMessage,
    rows.length,
    sortedRows,
  ]);

  return (
    <Box>
      <TableContainer className={className}>
        <TableDS>
          <TableHead
            headCells={headCells}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            tooltips={tooltips}
          />
          {renderBody()}
        </TableDS>
      </TableContainer>
      {rows.length > 0 && (
        <TablePagination
          component="div"
          count={rows.length}
          labelRowsPerPage="Rows per page"
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          data-testid="sorting-table-pagination"
        />
      )}
    </Box>
  );
};

export default hot(memo(Table));
